import React, { useMemo } from 'react';
import { Box, Typography, IconButton, darken, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { History } from '@mui/icons-material';
import useAppTheme from 'hooks/useAppTheme';
import { type SessionMessageType, type SessionType } from '../Chatbot';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface SessionMapProps {
  session: SessionType | null;
  activeMessageId: string | null;
  onScrollTo: (messageId: string) => void;
  onClose: () => void;
}

export const SessionHistory: React.FC<SessionMapProps> = ({
  session,
  activeMessageId,
  onScrollTo,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const userMessages = useMemo(() => {
    if (session?.messages) {
      const temp = session?.messages?.filter(
        (msg: SessionMessageType) => msg?.role === 'user'
      );
      return temp;
    }
    return [];
  }, [session?.messages]);

  return (
    <Box
      sx={{
        flex: 0.3,
        backgroundColor: enableDarkTheme ? 'rgba(26, 32, 44, 0.7)' : '#E8F0FF',
        overflowY: 'auto',
        transition: 'flex 0.3s ease',
        boxShadow: enableDarkTheme
          ? '0 0 10px rgba(0,0,0,0.4)'
          : '0 2px 6px rgba(0,0,0,0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          gap: 2,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.5,
            borderBottom: (theme) =>
              `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.08)'
              }`,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(21,38,66,0.9)'
                : 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(6px)',
            borderRadius: 1,
          }}
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            gap={1}
          >
            <History />
            <Typography
              sx={{
                fontWeight: 500,
              }}
            >
              {t('session_history')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box display={'flex'} flexDirection={'column'} gap={0.5}>
          {userMessages.map((msg, idx) => {
            const isActive = msg.messageId === activeMessageId;

            return (
              <Typography
                key={msg.messageId}
                onClick={() => {
                  onScrollTo(msg.messageId);
                }}
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  transition: 'all 0.1s ease',
                  ...(enableDarkTheme
                    ? {
                        color: isActive ? '#FFFFFF' : '#8A92A0',
                        backgroundColor: isActive ? '#4682B4' : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive
                            ? darken('#4682B4', 0.1)
                            : (theme) =>
                                alpha(theme.palette.text.primary, 0.04),
                          color: '#FFFFFF',
                        },
                      }
                    : {
                        color: isActive ? '#FFFFFF' : '#4A5568',
                        backgroundColor: isActive ? '#3666EC' : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive
                            ? darken('#3666EC', 0.1)
                            : (theme) =>
                                alpha(theme.palette.text.primary, 0.04),
                        },
                      }),
                }}
              >
                {`Q${idx + 1}: ${msg.content.data}`}
              </Typography>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
